<template>
  <ShForm class="contact-form">
    <TextInput
      v-if="!currentUser"
      :label="$t('CONTACT_US_NAME_LABEL')"
      id="name"
      name="name"
      v-model="name"
      v-validate="'required|max:200'"
      :errorMessage="errors.first('name')"
    />
    <TextInput
      v-if="!currentUser"
      :label="$t('CONTACT_US_EMAIL_LABEL')"
      id="email"
      name="email"
      v-model="email"
      v-validate="'required|email'"
      :errorMessage="errors.first('email')"
      data-vv-validate-on="blur"
    />
    <ShSelect
      :label="$t('ENQUIRY_LEVEL_ONE_HEADER')"
      id="inquiryType"
      name="inquiryType"
      v-model="inquiryType"
      v-validate="'required'"
      :defaultLabel="$t('SELECT_OPTION')"
      :options="inquiryTypes"
      :errorMessage="errors.first('inquiryType')"
    />
    <ShSelect
      v-if="inquiryReasons"
      :label="$t('ENQUIRY_LEVEL_TWO_HEADER')"
      id="reason"
      name="reason"
      v-model="reason"
      v-validate="'required'"
      :defaultLabel="$t('SELECT_OPTION')"
      :options="inquiryReasons"
      :errorMessage="errors.first('reason')"
    />
    <ShSelect
      v-if="inquiryVenues"
      :label="$t('ENQUIRY_LEVEL_THREE_HEADER')"
      id="venueName"
      name="venueName"
      v-model="venueName"
      v-validate="'required'"
      :options="inquiryVenues"
      :defaultLabel="$t('SELECT_OPTION')"
      :errorMessage="errors.first('venueName')"
    />
    <TextInput
      v-for="field in additionalFormFields"
      :key="field.name"
      :id="field.name"
      :name="field.name"
      :label="$t(inquiryTypei18nByKey(field.name))"
      v-model="additionalFields[field.name]"
      v-validate="fieldValidationRules(field)"
      :errorMessage="errors.first(field.name)"
      data-vv-validate-on="blur"
    />
    <ShTextarea
      class="contact-form__message"
      :label="$t('CONTACT_US_MESSAGE_LABEL')"
      id="body"
      name="body"
      v-model="body"
      v-validate="'required|max:200'"
      :errorMessage="errors.first('body')"
    />
    <template #buttons>
      <CheckBox
        v-if="!currentUser"
        name="privacyPolicy"
        v-model="privacyPolicy"
        v-validate="'required|isTrue'"
        :errorMessage="errors.first('privacyPolicy')"
      >
        <span>{{ $t('INQUIRY_ACCEPT_TERMS_AND_CONDITIONS_LABEL') }}
          <nuxt-link
            class="contact-form__terms"
            :to="localePath({ name: 'sitecore-jss', params: { route: 'terms-and-policies/privacy-policy' } })"
          >
            {{ $t('INQUIRY_ACCEPT_TERMS_AND_CONDITIONS_CTA') }}
          </nuxt-link>
        </span>
      </CheckBox>
      <ShButton
        class="contact-form__submit"
        :fullWidth="true"
        @click="handleSubmit"
      >
        {{ $t('CONTACT_US_SUBMIT_BUTTON_TEXT') }}
      </ShButton>
      <p v-if="error" class="contact-form__error">
        {{ $t('INQUIRY_ERROR_SUBMIT_FAILED') }}
      </p>
      <RecaptchaTerms class="contact-form__recaptcha" />
    </template>
  </ShForm>
</template>

<script>
import { mapGetters, createNamespacedHelpers } from 'vuex'
import {
  ShForm,
  TextInput,
  ShSelect,
  ShTextarea,
  CheckBox
} from '@sohohouse/design-system'

import get from '~/helpers/get'
import isEmptyObject from '~/helpers/is-empty-object'
import sanitizeObject from '~/helpers/sanitize-object'
import translationKeyGenerator from '~/utils/translationKeyGenerator'
import getFieldValidationRules from '~/utils/form'
import RecaptchaTerms from '~/components/recaptcha-terms'

const {
  mapGetters: mapInquiryGetters
} = createNamespacedHelpers('inquiries')

export default {
  components: {
    ShForm,
    ShSelect,
    ShTextarea,
    TextInput,
    CheckBox,
    RecaptchaTerms
  },

  $_veeValidate: {
    validator: 'new'
  },

  props: {
    isFormSaving: {
      type: Boolean,
      default: false
    },

    isFailed: {
      type: Boolean,
      default: false
    },

    error: {
      type: Object,
      default: null
    }
  },

  data () {
    const { $route: { query } } = this
    const { type, subType, venue, textId } = query

    return {
      name: '',
      email: '',
      inquiryType: type || '',
      reason: subType || '',
      venueName: venue || '',
      body: textId || '',
      contact: '',
      memberNumber: '',
      privacyPolicy: false,
      additionalFields: {}
    }
  },

  watch: {
    inquiryType (prevValue, value) {
      if (prevValue && value) {
        this.sanitizeFields(['reason', 'venueName', 'additionalFields'])
      }
    }
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapInquiryGetters(['inquiries']),
    canSubmit () {
      if (process.server) return false

      return !this.isFormSaving && !this.error && Object.values(this.veeValidateFields)
        .some(field => (field.required && field.pristine) || field.valid)
    },

    inquiryTypes () {
      return this.inquiries.map(({ id }) => ({
        value: id,
        label: this.$t(translationKeyGenerator('inquiry', id, 'type'))
      }))
    },

    inquiryReasons () {
      if (!this.inquiryType) return null

      const { id, reasons } = this.inquiryDetails
      const reasonField = this.$validator.fields.items.find(field => field.name === 'reason')

      if (reasonField) {
        // Assigning the validation rule to the reason field in case it's been removed below
        reasonField.rules = {
          required: []
        }

        // Removing errors related to 'reasons' field once the component is not displayed anymore
        // Also removed the validation rule itself in case the component is not present anymore to
        // avoid validating the non existing field
        if (this.errors && this.errors.items.length > 0) {
          reasonField.reset()
          reasonField.rules = {}
          this.$validator.errors.remove(reasonField.name, reasonField.scope)
        }
      }

      if (reasons && reasons.length > 0) {
        return reasons.map(reason => ({
          value: reason,
          label: this.$t(translationKeyGenerator('inquiry', id, reason))
        }))
      }

      return null
    },

    inquiryVenues () {
      if (!this.inquiryType) return null

      const { venues } = this.inquiryDetails

      if (venues && venues.length > 0) {
        return venues.map(venue => ({
          value: venue,
          label: venue
        }))
      }

      return null
    },

    inquiryDetails () {
      if (!this.inquiryType) return null

      return this.inquiries.find(({ id }) => id === this.inquiryType)
    },

    additionalFormFields () {
      const selected = this.inquiries.find(({ id }) => id === this.inquiryType)
      return get(selected, 'additionalFields', null)
    }
  },

  created () {
    // Validation rule for mandatory checkbox on public form
    this.$validator.extend('isTrue', {
      required: true,
      validate: value => value === true
    })
  },

  async mounted () {
    this.$validator.reset()
    this.$validator.localize(this.$i18n.locale, {
      messages: {
        required: this.$t('INQUIRY_ERROR_REQUIRED_FIELD'),
        max: (field, length) => this.$t('INQUIRY_ERROR_MAX_LENGTH', { length }),
        numeric: this.$t('INQUIRY_ERROR_NUMERIC')
      },
      custom: {
        privacyPolicy: {
          isTrue: this.$t('INQUIRY_ERROR_TERMS_AND_CONDITIONS')
        },
        contact_number: {
          regex: this.$t('INQUIRY_ERROR_PHONE_INVALID')
        }
      }
    })

    if (!isEmptyObject(this.$route.query)) {
      await this.$validator.validateAll()
    }
  },

  methods: {
    async handleSubmit () {
      const canSubmit = await this.$validator.validateAll()

      if (!canSubmit) return

      this.$emit('save', sanitizeObject(this.$data))
    },

    sanitizeFields (fieldNames) {
      fieldNames.forEach(fieldName => {
        this[fieldName] = typeof this[fieldName] === 'object' ? {} : ''
        const field = this.$validator.fields.items.find(({ name }) => fieldName === name)
        if (field) {
          field.reset()
        }
      })
    },

    inquiryTypei18nByKey (key) {
      return translationKeyGenerator('inquiry', this.inquiryType, key)
    },

    fieldValidationRules (field) {
      return getFieldValidationRules(field)
    }
  }
}
</script>

<style lang="scss" scoped>
  .contact-form {
    &__message {
      min-height: 200px;
      display: flex;
    }

    &__submit {
      margin-top: $space-050;
    }

    &__error {
      @include font('030');

      color: $color-alert;
      margin-top: $space-050;
    }

    &__terms {
      @include fontBold('040');
    }

    &__recaptcha {
      margin-top: $space-060;
    }
  }

</style>
